import { Suspense, useEffect, useState } from 'react';
import { Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import { CssBaseline, } from '@material-ui/core';
import RouterMainPage from "./components/RouterMainPage"
import { LineProgress } from './components/Overlay';
import { checkSign, signout, getPermision, permisionURL } from '../src/js/Domain';
import EmployeeRules from './EmployeeRules';
import Login from './Login';
import { ThemeSetProvider } from './components/ThemeSet';
import { PermissionItem, permissionPageInfo } from './js/types';
import { useBulletinApi } from './js/adapter/BulletinAdapter';
import ProgressBoard from './external/ProgressBoard';
import CarDeliveryConfirmForm from './external/CarDeliveryConfirmForm';
import { SnackbarProvider } from 'notistack';
import VersionChecker from './components/VersionChecker';
import { ApiProvider } from './js/apiProvider';
import { MainPageMaskProvider } from './js/MainPageMaskContent';
import { MainPageDialogProvider } from './js/MainPageDialogContext';
import { GlobalSnackbarCapture } from './js/GlobalSnackbar';

// Snackbar 顏色 class
const SnackBarClasses = {
    error: "SnackBarError",
    success: "SnackBarSuccess",
    warning: "SnackBarWarning",
    info: "SnackBarInfo",
}

const RouterSetting = () => {
    const location = useLocation();
    useEffect(() => {
        const init = async () => {
            // 若有客製化css 檔案 , 請在login_api_version 內設定 cssFileName 欄位, 名檔案需與css資料夾內的檔案名稱相同, 不包含副檔名
            // ex: "cssFileName": "audi",
            const response = await fetch(`${permisionURL}/login_api_version.json`);
            const jsonData = await response.json();
            const cssFileName = jsonData.cssFileName;
            try {
                await import(`./css/${cssFileName}.css`);
                console.log(`${cssFileName}.css has been loaded`);
            } catch (err) {
                // 若無客製css 檔案 , 請在則引入預設css檔案
                // @ts-ignore - allow importing CSS fallback without a .d.ts for css modules
                await import(`./css/index.css`);
            }
        }

        init()
    }, [])

    const origUrl = (location.pathname + location.search)
    //不携帶訊息就不需要重導
    const needToRedirect = !(origUrl === '/Login' || origUrl === '/');

    return (

        <ApiProvider>
            <MainPageMaskProvider>
                <SnackbarProvider
                    maxSnack={4}
                    autoHideDuration={1000} //多久會消失，不給就會用預設的
                    anchorOrigin={{
                        vertical: 'bottom', // 垂直位置
                        horizontal: 'right', // 水平位置
                    }}
                    classes={{ // Snackbar 顏色設定
                        variantSuccess: SnackBarClasses.success,
                        variantError: SnackBarClasses.error,
                        variantWarning: SnackBarClasses.warning,
                        variantInfo: SnackBarClasses.info,
                    }}
                >
                    <GlobalSnackbarCapture />
                    <MainPageDialogProvider>
                        <VersionChecker />
                        <Suspense fallback={<LineProgress />}>
                            <Switch>
                                <Route exact path="/Login" component={Login} />
                                <Route exact path="/EmployeeRules" component={EmployeeRules} />
                                <Route path="/Index" component={Index} />
                                <Route exact path="/ProgressBoard" component={ProgressBoard} />
                                <Route exact path="/CarDeliveryConfirmForm" component={CarDeliveryConfirmForm} />
                                <Route
                                    path="/CarWelcomeWeb"
                                    component={() => {
                                        window.location.href = location.pathname + location.search;
                                        return null;
                                    }}
                                />
                                <Route
                                    path="/ConferenceRoomExternal"
                                    component={() => {
                                        window.location.href = location.pathname + location.search;
                                        return null;
                                    }}
                                />

                                {(sessionStorage.company_code === '' || sessionStorage.company_code === undefined)
                                    ? <Redirect
                                        to={needToRedirect ? {
                                            pathname: '/Login',
                                            search: `?redirect=${location.pathname + location.search}`,
                                        } : {
                                            pathname: '/Login',
                                        }}
                                    /> : <Route component={Index} />

                                }
                            </Switch>
                        </Suspense>
                    </MainPageDialogProvider>
                </SnackbarProvider>
            </MainPageMaskProvider>
        </ApiProvider>
    )
}

interface IndexStates {
    path: string,
    permision: PermissionItem | undefined,
    navbar: permissionPageInfo | undefined,
    sidebar: permissionPageInfo | undefined,
    isRouterLoaded: boolean,
    sidebarisOpen: boolean,
}

const initialState = {
    path: '',
    permision: undefined,
    navbar: undefined,
    sidebar: undefined,
    isRouterLoaded: false,
    sidebarisOpen: false,
}

const Index = () => {

    const history = useHistory();
    const location = useLocation();
    const bulletinApiManager = useBulletinApi();

    const [mainStates, setMainStates] = useState<IndexStates>(initialState);

    /**
     * @description 登出
     */
    const logout = async () => {
        // 報表-控制台-即時戰報的版型存下來
        const layoutType_Left = localStorage.getItem("layoutType_Left")
        const layoutType_Right = localStorage.getItem("layoutType_Right")
        // call api 然後導頁
        signout().then(() => {
            localStorage.setCookie = false;
            sessionStorage.clear();
            localStorage.clear();
            history.push('/Login');
        }).then(() => {
            // 清完之後再把版型存回 LocalStorage
            localStorage.setItem("layoutType_Left", layoutType_Left || '')
            localStorage.setItem("layoutType_Right", layoutType_Right || '')
        });
    }

    useEffect(() => {

        const init = async () => {
            checkSign().then(async loginRes => {
                if (loginRes.success) {
                    try {
                        // 一進畫面先查全部必須同意且尚未已讀的公告
                        const { data: resBullint } = await bulletinApiManager.BulletinSearch({
                            is_login_read: "Y",
                            is_read: "N"
                        });

                        if (resBullint?.bulletins?.length > 0) {
                            history.push('/EmployeeRules');
                        }

                        let p = await getPermision(loginRes.data.path);

                        const urlParams = new URLSearchParams(location.search);
                        const urlHomePage = urlParams.get('home_page');
                        //優先使用來自於url params的home_page資料
                        if (urlHomePage) {
                            sessionStorage.setItem("home_page", urlHomePage);
                        } else {
                            if (sessionStorage.getItem("home_page") === null) {
                                sessionStorage.setItem("home_page", localStorage.getItem("home_page") || '');
                            }
                        }

                        const this_page = p.items.find(e => e.name === sessionStorage.getItem("home_page")) ?
                            p.items.find(e => e.name === sessionStorage.getItem("home_page")) :
                            p.items.length > 0 ?
                                p.items[0] :
                                undefined;
                        if (this_page === undefined || this_page.children === undefined) {
                            alert("無權限，返回登入頁面");
                            logout();
                        } else {
                            // 確保 sessionStorage/localStorage 與實際使用的 module 一致
                            // （防止設定了使用者沒有權限的 home_page 時，import 路徑錯誤導致空白頁）
                            sessionStorage.setItem("home_page", this_page.name);
                            localStorage.setItem("home_page", this_page.name);
                            setMainStates(prev => ({
                                ...prev,
                                path: loginRes.data.path,
                                permision: p,
                                navbar: this_page.children.find(e => { return e.name === 'nav' }),
                                sidebar: this_page.children.find(e => { return e.name === 'sidebar' }),
                                isRouterLoaded: true,
                            }))
                        }
                        if (sessionStorage.user_simulate === undefined) {
                            localStorage.token = loginRes.data.token;
                        }
                    } catch (error) {
                        console.error(error);
                        //history.push('/Login');
                    }

                } else {
                    switch (loginRes.message) {
                        case "deviceCodeError":
                            alert("已從其他裝置登入，請重新登入");
                            break;
                        case "expiredError":
                            alert("偵測到此帳號閒置過久，請重新登入");
                            break;
                        case "authorizationError":
                            alert("登入發生錯誤，請聯絡開發者");
                            break;
                        default:
                            alert("請重新登入");
                            break;
                    }
                    logout();
                }
            });
        }
        if (bulletinApiManager) {
            init();
        }

        return () => {
            setMainStates(initialState);
        }
    }, [bulletinApiManager])

    if (!mainStates.isRouterLoaded) return <LineProgress />
    if (mainStates.navbar === undefined) {
        alert(`沒有${mainStates.permision?.items[0].name}平台權限`);
    } else if (mainStates.sidebar === undefined) {
        alert(`沒有${mainStates.permision?.items[0].name}側邊欄權限`);
    }

    return (
        <>{
            mainStates.permision?.items !== undefined && <>
                <CssBaseline />
                <ThemeSetProvider>
                    <RouterMainPage
                        sidebarRes={mainStates.sidebar}
                        navRes={mainStates.navbar ? mainStates.navbar : []}
                        isRouterLoaded={mainStates.isRouterLoaded}
                        history={history}
                    />
                </ThemeSetProvider>
            </>
        }</>
    );

}

export default RouterSetting;
